import { SurfaceTone } from './labelGroundTone'

// Whether a label can be set as bare type on the ground it stands on, or needs a plate under it.
//
// A question about the GROUND rather than about whether a plan overlay is present, which is what
// makes a plan sheet and an orthomosaic of the same drawing behave alike. Two distinct failures:
//
//   split — the label lies across a boundary, so whichever ink wins leaves the other half
//           leaning on its casing alone.
//   busy  — the ground is both poles at glyph scale. Drafting linework on white paper averages
//           to a light grey, but the strokes between the glyphs are darker than the ink, and a
//           casing separates type from ONE ground, not from both at once.
//
// Neither is visible in the mean, which is why the mean alone could never have driven this.
export interface ICellReading {
  // Which ink wins this cell, scored by APCA.
  tone: SurfaceTone
  // Spread of linear luminance within the cell — see IGroundRaster.contrast.
  contrast: number
}

export interface IPlateDecision {
  // The ink for a label that ends up bare. A plated label's ink is constant and the caller
  // supplies it; this is still reported so the caller need not re-derive it if the plate is
  // later refused (a markup label, say, which is never plated).
  tone: SurfaceTone
  plated: boolean
}

// Share of cells that must agree before one ink is taken to serve the whole label.
const AGREEMENT = 0.7
// Linear-luminance spread above which a casing has nothing to separate.
//
// The one number that cannot be derived — the cell grid subsamples, so it reads low on precisely
// the thin strokes it exists to catch, and it was measured instead. It sits above everything a
// drone orthomosaic produced and below a plan sheet's drawing area, leaving the sheet's blank
// margins bare: dark ink on empty white paper needs no plate, only the drawing does.
const BUSY = 0.18
// Coming OFF a plate is deliberately harder than going on: keeping one it no longer strictly
// needs is stable and readable, while gaining and losing one as the camera moves re-lays-out the
// symbol and changes the collision box its neighbours placed against. The asymmetry is what
// turns a flicker into stickiness, and it fails in the direction of holding the plate.
const CALM = 0.12

export function decidePlate(
  cells: ICellReading[],
  previous?: IPlateDecision,
): IPlateDecision | null {
  // Nothing read: decide nothing rather than inventing an answer, and let the caller keep
  // whatever the label already wears.
  if (!cells.length) return previous ?? null

  let light = 0
  let contrast = 0
  for (const cell of cells) {
    if (cell.tone === 'light') light++
    contrast += cell.contrast
  }
  const meanContrast = contrast / cells.length
  const tone: SurfaceTone = light * 2 >= cells.length ? 'light' : 'dark'
  const agreement = Math.max(light, cells.length - light) / cells.length

  const split = agreement < AGREEMENT
  const busy = meanContrast > BUSY

  // Already plated: hold it unless the ground is now both unanimous and calm.
  if (previous?.plated) {
    const settled = agreement >= 1 && meanContrast <= CALM
    return { tone, plated: !settled }
  }

  return { tone, plated: split || busy }
}
